import React, { useEffect, useState } from 'react';
import apiClient from '../api';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import AssignmentIcon from '@mui/icons-material/Assignment';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import GavelIcon from '@mui/icons-material/Gavel';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PercentIcon from '@mui/icons-material/Percent';
import ArticleIcon from '@mui/icons-material/Article';
import { formatToman } from '../labels';
import { brand, glass } from '../theme';
import { Reveal } from '../components/Reveal';

function StatCard({ icon, label, value, accent, delay = 0 }) {
    return (
        <Reveal delay={delay} y={14} sx={{ ...glass.card, p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
                width: 46, height: 46, borderRadius: '50%', display: 'flex', alignItems: 'center',
                justifyContent: 'center', bgcolor: accent ? brand.accentTint : brand.primaryTint,
                color: accent ? brand.accentDeep : brand.primary, flexShrink: 0,
            }}>
                {icon}
            </Box>
            <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{value}</Typography>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
            </Box>
        </Reveal>
    );
}

function AdminOverview() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        apiClient.get('/api/admin-panel/stats/')
            .then((res) => setStats(res.data))
            .catch(() => setError('خطا در دریافت آمار پلتفرم.'))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
    }
    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    const totalUsers = (stats.users.requesters || 0) + (stats.users.providers || 0);
    const activeRequests = (stats.requests.pending || 0) + (stats.requests.active || 0);

    return (
        <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>نمای کلی پلتفرم</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>وضعیت لحظه‌ای الو یارا</Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
                <StatCard delay={0.00} icon={<GroupIcon />} label={`کاربران (${stats.users.requesters.toLocaleString('fa-IR')} خانواده / ${stats.users.providers.toLocaleString('fa-IR')} همیار)`} value={totalUsers.toLocaleString('fa-IR')} />
                <StatCard delay={0.04} icon={<AssignmentIcon />} label="درخواست‌های فعال/در انتظار" value={activeRequests.toLocaleString('fa-IR')} />
                <StatCard delay={0.08} icon={<VerifiedUserIcon />} label="همیاران در انتظار تایید" value={stats.pending_verifications.toLocaleString('fa-IR')} accent={stats.pending_verifications > 0} />
                <StatCard delay={0.12} icon={<GavelIcon />} label="قراردادهای در انتظار امضا" value={stats.pending_contracts.toLocaleString('fa-IR')} accent={stats.pending_contracts > 0} />
                <StatCard delay={0.16} icon={<AssignmentIcon />} label="درخواست‌های تکمیل‌شده" value={stats.requests.completed.toLocaleString('fa-IR')} />
                <StatCard delay={0.20} icon={<PaymentsIcon />} label="پرداخت‌های موفق (زرین‌پال)" value={formatToman(stats.paid_revenue_rial)} />
                <StatCard delay={0.24} icon={<AccountBalanceWalletIcon />} label="در انتظار تسویه با همیاران" value={formatToman(stats.pending_payouts_rial)} accent={stats.pending_payouts_rial > 0} />
                <StatCard delay={0.28} icon={<AccountBalanceWalletIcon />} label="واریزشده به همیاران" value={formatToman(stats.released_payouts_rial)} />
                <StatCard delay={0.32} icon={<PercentIcon />} label={`کارمزد پلتفرم (نرخ فعلی ${stats.commission_percent}٪)`} value={formatToman(stats.commission_earned_rial)} />
                <StatCard delay={0.36} icon={<ArticleIcon />} label="مقالات منتشرشده" value={stats.articles_published.toLocaleString('fa-IR')} />
            </Box>

            {stats.pending_verifications > 0 && (
                <Alert severity="warning">
                    {stats.pending_verifications.toLocaleString('fa-IR')} همیار در انتظار تایید هستند — به بخش «تایید همیاران» بروید.
                </Alert>
            )}
        </Box>
    );
}

export default AdminOverview;
